import { Link } from 'react-router'
import {
	hasPublishableLicensing,
	licensingStatusMeta,
	type CategoryLicensing,
} from '#app/scoring/licensing.ts'

/**
 * What the state does and does not check, for one trade.
 *
 * Renders nothing at all when the category has no researched status. Silence
 * is the correct output for an unresearched trade: a regulatory claim nobody
 * checked is worse than no claim, and it is the sort of error a reader would
 * be right to never forgive.
 *
 * Every sentence here is either structural or comes from the database, so no
 * string in this file is specific to any vertical. Adding a category is a row
 * in the dashboard, not a change to this component.
 *
 * `fallbacks` is what we verify in place of a licence. It is passed in rather
 * than written per category because it is the same for all of them and derives
 * from data the site already holds.
 */
export function LicensingBlock({
	category,
	fallbacks,
}: {
	category: CategoryLicensing
	fallbacks?: string[]
}) {
	if (!hasPublishableLicensing(category)) return null

	const meta = licensingStatusMeta(category.licensingStatus)
	const unlicensed = category.licensingStatus === 'unlicensed'

	return (
		<section className="my-10 rounded-lg border p-5">
			<h2 className="mb-1 text-lg font-medium tracking-tight">
				{meta.heading}
			</h2>
			<p className="text-muted-foreground text-sm leading-relaxed">
				{meta.description}
			</p>

			{category.licensingBoard ? (
				<p className="mt-3 text-sm leading-relaxed">
					Licensed by{' '}
					{category.licensingBoardUrl ? (
						<a
							className="underline"
							href={category.licensingBoardUrl}
							rel="noopener noreferrer"
							target="_blank"
						>
							{category.licensingBoard}
						</a>
					) : (
						category.licensingBoard
					)}
					.
					{category.licensingVerifyUrl ? (
						<>
							{' '}
							<a
								className="underline"
								href={category.licensingVerifyUrl}
								rel="noopener noreferrer"
								target="_blank"
							>
								Look up a licence
							</a>{' '}
							before you hire.
						</>
					) : null}
				</p>
			) : null}

			{category.licensingNote ? (
				<p className="mt-3 text-sm leading-relaxed">{category.licensingNote}</p>
			) : null}

			{/*
			 * Only shown where there is no licence to check. Where one exists, the
			 * licence is the stronger signal and listing our own checks alongside it
			 * would muddy which one a reader should rely on.
			 */}
			{unlicensed && fallbacks?.length ? (
				<div className="mt-4">
					<p className="text-sm font-medium">What we check instead</p>
					<ul className="text-muted-foreground mt-1 list-disc space-y-0.5 pl-5 text-sm">
						{fallbacks.map((f) => (
							<li key={f}>{f}</li>
						))}
					</ul>
				</div>
			) : null}

			{/*
			 * Deliberately not pluralised from the category name. "Lawn Care" and
			 * "Pest Control" do not take an -s, and a component that must stay
			 * category-neutral cannot be quietly assuming English noun forms.
			 */}
			<p className="text-muted-foreground mt-4 text-xs">
				This describes the trade, not any one business.{' '}
				<Link className="underline" to="/scoring-method">
					How we score businesses
				</Link>
				.
			</p>
		</section>
	)
}
